import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface FilterChipOption<TValue extends string> {
  value: TValue;
  label: string;
}

interface FilterChipsProps<TValue extends string> {
  /** Optional label rendered before the chips. */
  label?: string;
  options: ReadonlyArray<FilterChipOption<TValue>>;
  value: TValue | null;
  onChange: (value: TValue | null) => void;
  /** Optional extra slot rendered to the right of the chips
   * (e.g. a reset button or a search field). */
  trailing?: ReactNode;
  className?: string;
}

/**
 * Single-select chip group. Click an active chip to clear the
 * filter (matches the API Keys segmented filter mental model).
 * Renders nothing-tasteful — no shadows, no gradients.
 */
export function FilterChips<TValue extends string>({
  label,
  options,
  value,
  onChange,
  trailing,
  className,
}: FilterChipsProps<TValue>) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-2 rounded-md border border-ocean-100 bg-ocean-50/30 px-2 py-1.5",
        className
      )}
    >
      {label && (
        <span className="px-1 text-xs font-medium uppercase tracking-wide text-ocean-600">
          {label}
        </span>
      )}
      <div className="flex flex-wrap gap-1">
        {options.map((option) => {
          const isActive = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange(isActive ? null : option.value)}
              aria-pressed={isActive}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                isActive
                  ? "bg-white text-ocean-900 shadow-sm"
                  : "text-neutral-600 hover:bg-white/60 hover:text-ocean-800"
              )}
            >
              {option.label}
              {isActive && <X size={10} className="text-ocean-400" />}
            </button>
          );
        })}
      </div>
      {trailing && <div className="ml-auto">{trailing}</div>}
    </div>
  );
}
